import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar,
  FormControl,
  Select,
  Stack,
  MenuItem,
  Tab,
  Tabs,
} from '@mui/material';
import { toast } from 'react-toastify';

import BudgetForecastCashflowTable from './BudgetForecastCashflowTable';
import ProjectForecast from './projectforecast';
import { creators as DefaultActions } from '../../Reducers/Reporting/Default';
import ManageReportDataApi from '../../Api/ApiManageReportData';
import appdata from '../../Services/appdata';
import './style.css';

const tableName = 'Budget / Forecast / Cashflow';

const years = [
  new Date().getFullYear() - 4,
  new Date().getFullYear() - 3,
  new Date().getFullYear() - 2,
  new Date().getFullYear() - 1,
  new Date().getFullYear(),
  new Date().getFullYear() + 1,
  new Date().getFullYear() + 2,
];

export default function BudgetForecastCashflow() {
  const defaultInfo = useSelector(
    (state) => state.Reporting.Default.defaultInfo,
  );
  const dispatch = useDispatch();
  const [selectedCompany, setSelectedCompany] = useState(-1);
  const [selectedOrganisation, setSelectedOrganisation] = useState(-1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedForecastType, setSelectedForecastType] = useState('Budget');
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [chartofaccounts, setChartofaccounts] = useState([]);
  const [tabledata, setTabledata] = useState([]);

  useEffect(() => {
    const myFunc = async () => {
      let comps = [],
        pros = [],
        orgs = [],
        coa = [],
        tdata = [];

      dispatch(DefaultActions.defaultGetRequest());

      await Promise.all([
        ManageReportDataApi.getCompanies()
          .then((res) => {
            if (res.status === 200) comps = res.data;
            else toast.error(res.message);
          })
          .catch((error) => toast.error(error.message)),
        ManageReportDataApi.getOrganizations()
          .then((res) => {
            if (res.status === 200) orgs = res.data;
            else toast.error(res.message);
          })
          .catch((error) => toast.error(error.message)),
        ManageReportDataApi.getChartOfAccouts()
          .then((res) => {
            if (res.status === 200) coa = res.data;
            else toast.error(res.message);
          })
          .catch((error) => toast.error(error.message)),
        ManageReportDataApi.getProjects()
          .then((res) => {
            if (res.status === 200) pros = res.data;
            else toast.error(res.message);
          })
          .catch((error) => toast.error(error.message)),
        ManageReportDataApi.getTemplate(tableName)
          .then((res) => {
            if (res.status === 200) {
              if (res.data && res.data.length > 0) {
                if ('DimChartOfAccountsKey' in res.data[0])
                  tdata = res.data.sort((a, b) =>
                    a.DimChartOfAccountsKey < b.DimChartOfAccountsKey ? -1 : 1,
                  );
                else
                  tdata = res.data.sort((a, b) =>
                    a.DimProjectKey < b.DimProjectKey ? -1 : 1,
                  );
              }
            } else toast.error(res.message);
          })
          .catch((error) => toast.error(error.message)),
      ]);

      pros = pros.sort((a, b) => (a[0] < b[0] ? -1 : 1));
      const company_org_id = appdata.getItem('company_org_id');
      let selected_company = company_org_id
        ? company_org_id
        : comps && comps.length === 1
        ? comps[0].id
        : -1;
      let selected_organisation = orgs.find(
        (org) => org.DimCompanyKey === selected_company,
      )
        ? orgs.find((org) => org.DimCompanyKey === selected_company).id
        : -1;

      let selected_year =
        new Date().getMonth() + 1 >= defaultInfo.FiscalYearStartMonth
          ? new Date().getFullYear() + 1
          : new Date().getFullYear();

      setCompanies(comps);
      setOrganisations(orgs);
      setChartofaccounts(coa);
      setProjects(pros);
      setSelectedCompany(selected_company);
      setSelectedYear(selected_year);
      setSelectedOrganisation(selected_organisation);
      setTabledata(tdata);
    };

    myFunc();
  }, [dispatch, defaultInfo.FiscalYearStartMonth]);

  const getStateData = (data) => {
    if (selectedForecastType !== 'ProjectForecast')
      setTabledata(
        data.sort((a, b) =>
          a.DimChartOfAccountsKey < b.DimChartOfAccountsKey ? -1 : 1,
        ),
      );
    else setTabledata(data);
  };

  const handleRefetchAccounts = () => {
    ManageReportDataApi.getChartOfAccouts()
      .then((res) => {
        if (res.status === 200) setChartofaccounts(res.data);
        else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const handleRefetchProjects = () => {
    ManageReportDataApi.getProjects()
      .then((res) => {
        if (res.status === 200) setProjects(res.data);
        else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const handleSelectCompany = async (ev) => {
    appdata.setItem('company_org_id', ev.target.value);
    setSelectedCompany(ev.target.value);

    let coa = [],
      tdata = [];

    dispatch(DefaultActions.defaultGetRequest());

    await Promise.all([
      ManageReportDataApi.getChartOfAccouts()
        .then((res) => {
          if (res.status === 200) coa = res.data;
          else toast.error(res.message);
        })
        .catch((error) => toast.error(error.message)),
      ManageReportDataApi.getTemplate(selectedForecastType === 'ProjectForecast' ? 'Project Forecast' : tableName)
        .then((res) => {
          if (res.status === 200) {
            if (res.data && res.data.length > 0) {
              if (selectedForecastType !== 'ProjectForecast')
                tdata = res.data.sort((a, b) =>
                  a.DimChartOfAccountsKey < b.DimChartOfAccountsKey ? -1 : 1,
                );
              else
                tdata = res.data.sort((a, b) =>
                  a.DimProjectKey < b.DimProjectKey ? -1 : 1,
                );
            }
          } else toast.error(res.message);
        })
        .catch((error) => toast.error(error.message)),
    ]);

    const company_org_id = ev.target.value;
    let selected_company = company_org_id
      ? company_org_id
      : companies && companies.length === 1
      ? companies[0].id
      : -1;
    let selected_organisation = organisations.find(
      (org) => org.DimCompanyKey === selected_company,
    )
      ? organisations.find((org) => org.DimCompanyKey === selected_company).id
      : -1;

    let selected_year =
      new Date().getMonth() + 1 >= defaultInfo.FiscalYearStartMonth
        ? new Date().getFullYear() + 1
        : new Date().getFullYear();

    setChartofaccounts(coa);
    setSelectedCompany(selected_company);
    setSelectedYear(selected_year);
    setSelectedOrganisation(selected_organisation);
    setTabledata(tdata);
  };

  const handleTabChange = (ev, tabValue) => {
    let forecastType = 'Budget';
    if (tabValue === 1) forecastType = 'Forecast';
    else if (tabValue === 2) forecastType = 'Cashflow';
    else if (tabValue === 3) forecastType = 'ProjectForecast';

    if (tabValue !== 3 && selectedForecastType === 'ProjectForecast')
      handleForecastType(forecastType, tableName);
    else if (tabValue === 3 && selectedForecastType !== 'ProjectForecast')
      handleForecastType(forecastType, 'ProjectForecast');

    setSelectedForecastType(forecastType);
  };
  
  const handleForecastType = (tabValue, tName) => {
    ManageReportDataApi.getTemplate(tName)
      .then((res) => {
        if (res.status === 200) {
          if (tabValue !== 3)
            res.data.sort((a, b) =>
              a.DimChartOfAccountsKey < b.DimChartOfAccountsKey ? -1 : 1,
            );
          else
            res.data.sort((a, b) =>
              a.DimProjectKey < b.DimProjectKey ? -1 : 1,
            );
          setTabledata(res.data);
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const onAddTemplate = (tableName, selectedTempId) => {
    ManageReportDataApi.addBudgetForecastCashFlow(
      tableName,
      selectedCompany,
      selectedOrganisation,
      selectedForecastType,
      selectedYear,
      selectedTempId,
    )
      .then((res) => {
        if (res.status === 200) {
          ManageReportDataApi.getTemplate(tableName)
            .then((bfsres) => {
              if (bfsres.status === 200) getStateData(bfsres.data);
              else toast.error(bfsres.message);
            })
            .catch((error) => toast.error(error.message));
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const onDeleteTemplate = (tableName, rowIndex) => {
    ManageReportDataApi.deleteTemplate(tableName, [rowIndex])
      .then((res) => {
        if (res.status === 200) {
          ManageReportDataApi.getTemplate(tableName)
            .then((bfsres) => {
              if (bfsres.status === 200) getStateData(bfsres.data);
              else toast.error(bfsres.message);
            })
            .catch((error) => toast.error(error.message));
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const onDeleteAllTemplate = async (
    tableName,
    selectedForecastType,
    selectedYear,
    selectedCompany,
  ) => {
    ManageReportDataApi.deleteAllTemplate(
      tableName,
      selectedForecastType,
      selectedYear,
      selectedCompany,
    )
      .then((res) => {
        if (res.status === 200) {
          ManageReportDataApi.getTemplate(tableName)
            .then((bfsres) => {
              if (bfsres.status === 200) getStateData(bfsres.data);
              else toast.error(bfsres.message);
            })
            .catch((error) => toast.error(error.message));
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const handleClickCopy = async (type) => {
    ManageReportDataApi.copyFromBudget(type, selectedYear)
      .then((res) => {
        if (res.status === 200) {
          ManageReportDataApi.getTemplate(tableName)
            .then((bfsres) => {
              if (bfsres.status === 200) getStateData(bfsres.data);
              else toast.error(bfsres.message);
            })
            .catch((error) => toast.error(error.message));
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const tableRows = useMemo(() => {
    if (tabledata) {
      let table_rows = [];
      tabledata.forEach((row) => {
        if (selectedForecastType !== 'ProjectForecast') {
          if (
            selectedYear === row.Year &&
            selectedForecastType === row.ForecastType &&
            selectedCompany === row.DimCompanyKey &&
            selectedOrganisation === row.DimOrganisationKey
          )
            table_rows.push(row);
        } else {
          if (
            selectedYear === row.Year &&
            selectedCompany === row.DimCompanyKey &&
            selectedOrganisation === row.DimOrganisationKey
          )
            table_rows.push(row);
        }
      });
      return table_rows;
    } else return [];
  }, [
    tabledata,
    selectedCompany,
    selectedOrganisation,
    selectedYear,
    selectedForecastType,
  ]);

  const filterChartOfAccounts = useMemo(() => {
    if (
      selectedForecastType === 'Budget' ||
      selectedForecastType === 'Forecast'
    ) {
      return chartofaccounts.filter((account) => account[6] === 'P&L');
    } else if (selectedForecastType === 'Cashflow') {
      return chartofaccounts;
    } else return [];
  }, [chartofaccounts, selectedForecastType]);
  
  const tabIdx = useMemo(() => {
    if (selectedForecastType === 'Budget') return 0;
    else if (selectedForecastType === 'Forecast') return 1;
    else if (selectedForecastType === 'Cashflow') return 2;
    else return 3;
  }, [selectedForecastType]);

  return (
    <div className="budget-forecast">
      <Stack direction="row" alignItems="center" spacing={1}>
        <label style={{ lineHeight: 1 }}>Company:</label>
        <FormControl variant="standard" sx={{ minWidth: 80 }}>
          <Select value={selectedCompany} onChange={handleSelectCompany}>
            <MenuItem value={-1} disabled>
              {' '}
            </MenuItem>
            {companies &&
              companies.map(
                (cur) =>
                  cur.IsActive && (
                    <MenuItem key={cur.id} value={cur.id}>
                      {cur.CompanyName}
                    </MenuItem>
                  ),
              )}
          </Select>
        </FormControl>
        <label style={{ lineHeight: 1 }}>Organisation:</label>
        <FormControl variant="standard" sx={{ minWidth: 80 }}>
          <Select
            value={selectedOrganisation}
            onChange={(ev) => setSelectedOrganisation(ev.target.value)}
          >
            <MenuItem value={-1} disabled>
              {' '}
            </MenuItem>
            {organisations &&
              organisations.map(
                (cur) =>
                  selectedCompany === cur.DimCompanyKey &&
                  cur.IsActive && (
                    <MenuItem key={cur.id} value={cur.id}>
                      {cur.OrgName}
                    </MenuItem>
                  ),
              )}
          </Select>
        </FormControl>
        <label style={{ lineHeight: 1 }}>Financial Year:</label>
        <FormControl variant="standard" sx={{ minWidth: 80 }}>
          <Select
            value={selectedYear}
            onChange={(ev) => setSelectedYear(ev.target.value)}
          >
            {years &&
              years.map((cur) => (
                <MenuItem key={cur} value={cur}>
                  {cur}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Stack>
      <div
        style={{
          width: '100%',
          marginTop: 10,
          height: '84vh',
        }}
      >
        <AppBar position="static" color="default">
          <Tabs
            value={tabIdx}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            aria-label="scrollable auto tabs example"
          >
            <Tab label="Budget" id={0} />
            <Tab label="Forecast" id={1} />
            <Tab label="Cashflow" id={2} />
            <Tab label="Project Forecast" id={3} />
          </Tabs>
        </AppBar>
        {tabIdx === 3 ? (
          <ProjectForecast
            name="Project Forecast"
            rows={tableRows}
            projects={projects}
            selectedForecastType={selectedForecastType}
            selectedYear={selectedYear}
            selectedCompany={selectedCompany}
            selectedOrganisation={selectedOrganisation}
            onClickedAdd={onAddTemplate}
            onClickedDelete={onDeleteTemplate}
            onClickedAllDelete={onDeleteAllTemplate}
            getStateData={getStateData}
            handleRefetchProjects={handleRefetchProjects}
          />
        ) : (
          <BudgetForecastCashflowTable
            name={tableName}
            rows={tableRows}
            chartofaccounts={filterChartOfAccounts}
            selectedForecastType={selectedForecastType}
            selectedYear={selectedYear}
            selectedOrganisation={selectedOrganisation}
            selectedCompany={selectedCompany}
            onClickedAdd={onAddTemplate}
            onClickedDelete={onDeleteTemplate}
            onClickedAllDelete={onDeleteAllTemplate}
            getStateData={getStateData}
            onClickCopy={handleClickCopy}
            handleRefetchAccounts={handleRefetchAccounts}
          />
        )}
      </div>
    </div>
  );
}
